import React from "react";
import { Doughnut } from "react-chartjs-2";
import "chart.js/auto";

const PALETTE = ["#2A3E59", "#4a63ff", "#D9665B", "#F2C94C", "#0f8b8d", "#A6786D", "#898C1F", "#6d4fc4"];
const UNCATEGORIZED_COLOUR = "#c7ccd6";

const colourFor = (label, index) => (label === "Uncategorized" ? UNCATEGORIZED_COLOUR : PALETTE[index % PALETTE.length]);

const TsVenueModuleBreakdownDashboard = ({ loading = false, moduleBreakdown = [] }) => {
  const labels = moduleBreakdown.map((item) => item.module);
  const values = moduleBreakdown.map((item) => item.totalCount);
  const colours = labels.map((label, index) => colourFor(label, index));
  const total = values.reduce((sum, value) => sum + value, 0);

  return (
    <div className="relative flex flex-col gap-4 rounded-[24px] border border-[#e7ebf8] bg-[linear-gradient(180deg,#ffffff_0%,#fbfcff_100%)] px-6 pb-6 pt-5 shadow-[0_16px_34px_rgba(30,52,120,0.06)]">
      <div>
        <h3 className="mb-1 text-[1.1rem] font-semibold tracking-[-0.02em] text-[#1f2d4d]">Module Breakdown</h3>
        <p className="text-sm text-[#6b7894]">Which touchscreen modules are receiving the most content clicks.</p>
      </div>

      {loading ? (
        <div className="rounded-[18px] border border-[#e6ebf8] bg-[#fbfcff] px-4 py-4 text-sm text-[#7a88a6]">Loading module breakdown...</div>
      ) : moduleBreakdown.length === 0 ? (
        <div className="rounded-[18px] border border-[#e6ebf8] bg-[#fbfcff] px-4 py-4 text-sm text-[#7a88a6]">No module click data available for the selected period.</div>
      ) : (
        <div className="flex flex-col items-center gap-6 pt-2 sm:flex-row sm:items-center">
          <div className="relative h-[220px] w-[220px] flex-none">
            <Doughnut
              data={{
                labels,
                datasets: [
                  {
                    data: values,
                    backgroundColor: colours,
                    borderColor: "#ffffff",
                    borderWidth: 3,
                    hoverOffset: 6,
                  },
                ],
              }}
              options={{
                responsive: true,
                maintainAspectRatio: true,
                cutout: "68%",
                plugins: {
                  legend: { display: false },
                  datalabels: { display: false },
                  tooltip: {
                    callbacks: {
                      label: (ctx) => {
                        const pct = total ? ((ctx.parsed / total) * 100).toFixed(1) : "0.0";
                        return ` ${ctx.label}: ${ctx.parsed.toLocaleString()} clicks (${pct}%)`;
                      },
                    },
                  },
                },
              }}
            />
            <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
              <div className="text-[1.6rem] font-semibold leading-none text-[#1f2d4d]">{total.toLocaleString()}</div>
              <div className="mt-1 text-[0.7rem] font-medium uppercase tracking-[0.08em] text-[#8a97b4]">Total Clicks</div>
            </div>
          </div>

          <div className="flex w-full min-w-0 flex-1 flex-col gap-2">
            {moduleBreakdown
              .map((item, index) => ({ ...item, colour: colourFor(item.module, index) }))
              .sort((a, b) => b.totalCount - a.totalCount)
              .map((item) => {
                const pct = total ? ((item.totalCount / total) * 100).toFixed(1) : "0.0";
                return (
                  <div key={item.module} className="flex items-start gap-3 rounded-[14px] px-3 py-2 hover:bg-[#f8faff]">
                    <span className="mt-1.5 h-3 w-3 flex-none rounded-full" style={{ backgroundColor: item.colour }} />
                    <span className="min-w-0 flex-1 break-words text-sm font-medium leading-snug text-[#1f2d4d]">
                      {item.module}
                    </span>
                    <span className="flex-none text-sm font-semibold text-[#1f2d4d]">{item.totalCount.toLocaleString()}</span>
                    <span className="w-12 flex-none text-right text-xs text-[#8a97b4]">{pct}%</span>
                  </div>
                );
              })}
          </div>
        </div>
      )}
    </div>
  );
};

export default TsVenueModuleBreakdownDashboard;
